import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { NfcCardsContext, nfcCardsInitialState, TTypeOfModalWindow } from './nfcCardsContext';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	EditRestaurantContext,
	IEditRestaurantContext,
} from 'modules/CustomersAndRestaurants/Restaurants/EditRestaurant/editRestaurantState/editRestaurantContext';
import {
	IGetRestaurantNfcCards,
	IUpdateRestaurantNfcCards,
	IExtendedRestaurantNfcCards,
	NfcCardPermission,
} from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { nfcCardsReducer } from './nfcCardsReducer';
import { NfcCardsActionsTypes } from './nfcCardsActions';
import { getRestaurantNfcCard } from 'backend/restaurants';

export const NfcCardsState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(nfcCardsReducer, nfcCardsInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { restaurantId, visibleForm } = useContext<IEditRestaurantContext>(EditRestaurantContext);

	const needRequest = visibleForm === 'nfcCardsForm' && restaurantId;

	const { response, refetch, loading } = useFetchData<IGetRestaurantNfcCards, string>(
		needRequest ? getRestaurantNfcCard : null,
		restaurantId || '',
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		refetch();
	}, [needRequest]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: NfcCardsActionsTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: NfcCardsActionsTypes.SET_INITIAL_NFC_CARDS,
				payload: {
					initialNfcCards: response.data.nfcCards.map((nfcCard, i) => ({
						...nfcCard,
						id: String(i),
						nfcCardPermission: nfcCard.permissions.filter(
							(permission) => permission !== 'None', // This is an invalid value, but the backend might send it accidentally
						),
					})),
					nfcCards: response.data.nfcCards.map((nfcCard, i) => ({
						...nfcCard,
						id: String(i),
						nfcCardPermission: nfcCard.permissions.filter(
							(permission) => permission !== 'None', // This is an invalid value, but the backend might send it accidentally
						),
					})),
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: NfcCardsActionsTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const addNfcCardsBlock = useCallback(
		(value: IExtendedRestaurantNfcCards) => {
			const updatedNfcCardsBlocks = [...state.nfcCards, value];

			dispatch({
				type: NfcCardsActionsTypes.SET_NFC_CARDS,
				payload: {
					nfcCards: updatedNfcCardsBlocks,
				},
			});
		},
		[state.nfcCards],
	);

	const deleteNfcCardsBlock = useCallback(
		(value: string) => {
			const updatedBankSettingsBlocks = state.nfcCards.filter((el) => el.id !== value);

			dispatch({
				type: NfcCardsActionsTypes.SET_NFC_CARDS,
				payload: {
					nfcCards: updatedBankSettingsBlocks,
				},
			});
		},
		[state.nfcCards],
	);

	const changeUUIDCard = useCallback(
		(settingsId: string, value: string) => {
			const updatedNfcCardsBlocks = state.nfcCards.map((el) =>
				el.id === settingsId ? { ...el, uuid: value } : el,
			);

			dispatch({
				type: NfcCardsActionsTypes.SET_NFC_CARDS,
				payload: {
					nfcCards: updatedNfcCardsBlocks,
				},
			});
		},
		[state.nfcCards],
	);

	const changeFullName = useCallback(
		(settingsId: string, value: string) => {
			const updatedNfcCardsBlocks = state.nfcCards.map((el) =>
				el.id === settingsId ? { ...el, cardHolder: value } : el,
			);

			dispatch({
				type: NfcCardsActionsTypes.SET_NFC_CARDS,
				payload: {
					nfcCards: updatedNfcCardsBlocks,
				},
			});
		},
		[state.nfcCards],
	);

	const changeNfcCardPermission = useCallback(
		(settingsId: string, value: NfcCardPermission[]) => {
			const updatedNfcCardsBlocks = state.nfcCards.map((el) =>
				el.id === settingsId ? { ...el, permissions: value } : el,
			);

			dispatch({
				type: NfcCardsActionsTypes.SET_NFC_CARDS,
				payload: {
					nfcCards: updatedNfcCardsBlocks,
				},
			});
		},
		[state.nfcCards],
	);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateRestaurantNfcCards>) => {
		dispatch({
			type: NfcCardsActionsTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: NfcCardsActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<NfcCardsContext.Provider
			value={{
				refetchData,
				addNfcCardsBlock,
				deleteNfcCardsBlock,
				changeUUIDCard,
				changeFullName,
				changeNfcCardPermission,
				setDataForSubmit,
				changeTypeOfModalWindow,
				needRefetchData: state.needRefetchData,
				initialNfcCards: state.initialNfcCards,
				nfcCards: state.nfcCards,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</NfcCardsContext.Provider>
	);
};
